import { useState, useEffect } from 'preact/hooks';
import { useAuth } from '../lib/auth';
import { route } from 'preact-router';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

// Common countries outside EU
const NON_EU_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'NO', name: 'Norway' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SG', name: 'Singapore' },
  { code: 'JP', name: 'Japan' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'IL', name: 'Israel' }
];

export function CheckoutPage({ plan: planParam }) {
  const { user, subscription, workspace } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Plan data
  const [plan, setPlan] = useState(null);
  const [euCountries, setEuCountries] = useState([]);

  // Form state
  const [customerType, setCustomerType] = useState('business');
  const [companyName, setCompanyName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [countryCode, setCountryCode] = useState('MT');
  const [vatNumber, setVatNumber] = useState('');
  const [vatValid, setVatValid] = useState(false);
  const [vatValidating, setVatValidating] = useState(false);
  const [vatError, setVatError] = useState(null);
  const [vatCompanyName, setVatCompanyName] = useState(null);
  const [businessRegistration, setBusinessRegistration] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');

  // Pricing calculation
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // Check if country is in EU
  const isEUCountry = euCountries.some(c => c.country_code === countryCode);
  const isMalta = countryCode === 'MT';

  // Determine if VAT number field should be shown
  const showVatField = isEUCountry && customerType === 'business' && !isMalta;

  // Determine if business registration field should be shown (non-EU B2B)
  const showBusinessRegField = !isEUCountry && customerType === 'business';

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Recalculate pricing when billing details change
  useEffect(() => {
    if (plan) {
      calculatePricing();
    }
  }, [customerType, countryCode, vatValid, plan]);

  async function loadInitialData() {
    setLoading(true);
    try {
      const sessionId = localStorage.getItem('sessionId');

      // Determine which plan from URL param or default to pro
      const selectedPlan = planParam || new URLSearchParams(window.location.search).get('plan') || 'pro';

      // Fetch plans
      const plansRes = await fetch(`${API_URL}/v3/plans`);
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        const selectedPlanData = (plansData.plans || []).find(p => p.key === selectedPlan);
        if (selectedPlanData) {
          setPlan(selectedPlanData);
        }
      }

      // Fetch billing details and EU countries
      const billingRes = await fetch(`${API_URL}/v3/billing/details`, {
        headers: { 'Authorization': `Bearer ${sessionId}` }
      });

      if (billingRes.ok) {
        const billingData = await billingRes.json();
        setEuCountries(billingData.eu_countries || []);

        // Pre-fill form if existing billing details
        if (billingData.details) {
          const d = billingData.details;
          setCustomerType(d.customer_type || 'business');
          setCompanyName(d.company_name || '');
          setAddressLine1(d.address_line1 || '');
          setAddressLine2(d.address_line2 || '');
          setCity(d.city || '');
          setPostalCode(d.postal_code || '');
          setCountryCode(d.country_code || 'MT');
          setVatNumber(d.vat_number || '');
          setVatValid(d.vat_valid || false);
          setBusinessRegistration(d.business_registration || '');
          setContactEmail(d.contact_email || user?.email || '');
          setContactName(d.contact_name || user?.name || '');
        } else {
          // Set defaults from user
          setContactEmail(user?.email || '');
          setContactName(user?.name || '');
        }
      }
    } catch (err) {
      console.error('Failed to load checkout data:', err);
      setError('Failed to load checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function calculatePricing() {
    if (!plan) return;

    setPricingLoading(true);
    try {
      const sessionId = localStorage.getItem('sessionId');
      const res = await fetch(`${API_URL}/v3/billing/calculate-vat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          plan: plan.key,
          billing_details: {
            customerType,
            countryCode,
            vatNumber: showVatField ? vatNumber : null,
            vatValid: showVatField ? vatValid : false
          }
        })
      });

      if (res.ok) {
        const data = await res.json();
        setPricing(data);
      }
    } catch (err) {
      console.error('Failed to calculate pricing:', err);
    } finally {
      setPricingLoading(false);
    }
  }

  async function validateVAT() {
    if (!vatNumber || vatNumber.length < 5) {
      setVatError('Please enter a valid VAT number');
      setVatValid(false);
      return;
    }

    setVatValidating(true);
    setVatError(null);

    try {
      const sessionId = localStorage.getItem('sessionId');
      const res = await fetch(`${API_URL}/v3/billing/validate-vat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({ vat_number: vatNumber })
      });

      const data = await res.json();

      if (data.valid) {
        setVatValid(true);
        setVatCompanyName(data.name);
        setVatError(null);
        // Auto-fill company name if available
        if (data.name && !companyName) {
          setCompanyName(data.name);
        }
      } else {
        setVatValid(false);
        setVatCompanyName(null);
        setVatError(data.error || 'VAT number not found in VIES database');
      }
    } catch (err) {
      setVatError('Failed to validate VAT number');
      setVatValid(false);
    } finally {
      setVatValidating(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Validation
    if (!companyName.trim()) {
      setError(customerType === 'business' ? 'Company name is required' : 'Full name is required');
      return;
    }
    if (!addressLine1.trim()) {
      setError('Address is required');
      return;
    }
    if (!city.trim()) {
      setError('City is required');
      return;
    }
    if (!postalCode.trim()) {
      setError('Postal code is required');
      return;
    }
    if (!countryCode) {
      setError('Country is required');
      return;
    }
    if (!contactEmail.trim() || !contactEmail.includes('@')) {
      setError('Valid email is required');
      return;
    }

    setSubmitting(true);

    try {
      const sessionId = localStorage.getItem('sessionId');

      // Save billing details first
      const saveRes = await fetch(`${API_URL}/v3/billing/details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          customer_type: customerType,
          company_name: companyName,
          address_line1: addressLine1,
          address_line2: addressLine2,
          city,
          postal_code: postalCode,
          country_code: countryCode,
          vat_number: showVatField ? vatNumber : null,
          vat_valid: showVatField ? vatValid : false,
          business_registration: showBusinessRegField ? businessRegistration : null,
          contact_email: contactEmail,
          contact_name: contactName
        })
      });

      if (!saveRes.ok) {
        const data = await saveRes.json();
        throw new Error(data.error || 'Failed to save billing details');
      }

      // Create Stripe checkout session
      const checkoutRes = await fetch(`${API_URL}/v3/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({ plan: plan.key })
      });

      if (!checkoutRes.ok) {
        const data = await checkoutRes.json();
        throw new Error(data.error || 'Failed to create checkout session');
      }

      const checkoutData = await checkoutRes.json();
      if (checkoutData.checkout_url) {
        window.location.href = checkoutData.checkout_url;
      }
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  // Only owner can access checkout
  if (user && user.role !== 'owner') {
    return (
      <div class="checkout-page">
        <div class="checkout-denied">
          <h2>Access Denied</h2>
          <p>Only the workspace owner can manage billing.</p>
          <a href="/" class="btn btn-primary">Return to Dashboard</a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div class="checkout-page">
        <div class="loading-container">
          <div class="spinner" />
          <p>Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div class="checkout-page">
        <div class="checkout-denied">
          <h2>Plan Not Found</h2>
          <p>The selected plan could not be found.</p>
          <a href="/upgrade" class="btn btn-primary">View Plans</a>
        </div>
      </div>
    );
  }

  // Build country options
  const allCountries = [
    ...euCountries.map(c => ({ code: c.country_code, name: c.country_name, isEU: true })),
    ...NON_EU_COUNTRIES.map(c => ({ ...c, isEU: false }))
  ].sort((a, b) => {
    // Malta first, then alphabetical
    if (a.code === 'MT') return -1;
    if (b.code === 'MT') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div class="checkout-page">
      <div class="checkout-header">
        <a href="/upgrade" class="back-link">
          &larr; Back to plans
        </a>
        <h1>Complete your order</h1>
        <p>Enter your billing details to continue with the {plan.name} plan</p>
      </div>

      {error && (
        <div class="alert alert-error">
          {error}
        </div>
      )}

      <div class="checkout-container">
        <form onSubmit={handleSubmit} class="checkout-form">
          {/* Customer Type Toggle */}
          <div class="form-section">
            <label class="form-label">I'm purchasing as a</label>
            <div class="toggle-group">
              <button
                type="button"
                class={`toggle-btn ${customerType === 'business' ? 'active' : ''}`}
                onClick={() => setCustomerType('business')}
              >
                Business
              </button>
              <button
                type="button"
                class={`toggle-btn ${customerType === 'individual' ? 'active' : ''}`}
                onClick={() => setCustomerType('individual')}
              >
                Individual
              </button>
            </div>
          </div>

          {/* Company/Name */}
          <div class="form-group">
            <label class="form-label">
              {customerType === 'business' ? 'Company Name' : 'Full Name'} *
            </label>
            <input
              type="text"
              class="form-input"
              value={companyName}
              onInput={e => setCompanyName(e.target.value)}
              placeholder={customerType === 'business' ? 'Your Company Ltd' : 'John Smith'}
              required
            />
          </div>

          {/* Country */}
          <div class="form-group">
            <label class="form-label">Country *</label>
            <select
              class="form-select"
              value={countryCode}
              onChange={e => {
                setCountryCode(e.target.value);
                // Reset VAT when country changes
                setVatNumber('');
                setVatValid(false);
                setVatError(null);
              }}
            >
              <optgroup label="EU Countries">
                {allCountries.filter(c => c.isEU).map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </optgroup>
              <optgroup label="Other Countries">
                {allCountries.filter(c => !c.isEU).map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* VAT Number (EU B2B only, not Malta) */}
          {showVatField && (
            <div class="form-group">
              <label class="form-label">
                VAT Number
                <span class="form-hint">(Optional - For reverse charge)</span>
              </label>
              <div class="input-with-button">
                <input
                  type="text"
                  class={`form-input ${vatValid ? 'is-valid' : ''} ${vatError ? 'is-invalid' : ''}`}
                  value={vatNumber}
                  onInput={e => {
                    setVatNumber(e.target.value.toUpperCase());
                    setVatValid(false);
                    setVatError(null);
                  }}
                  placeholder={`${countryCode}123456789`}
                />
                <button
                  type="button"
                  class="btn btn-secondary"
                  onClick={validateVAT}
                  disabled={vatValidating || vatNumber.length < 5}
                >
                  {vatValidating ? 'Checking...' : 'Validate'}
                </button>
              </div>
              {vatValid && (
                <div class="vat-success">
                  VAT number valid
                  {vatCompanyName && <span> - {vatCompanyName}</span>}
                </div>
              )}
              {vatError && (
                <div class="vat-error">{vatError}</div>
              )}
              <p class="form-help">
                Enter your EU VAT number to apply reverse charge (0% VAT)
              </p>
            </div>
          )}

          {/* Business Registration (Non-EU B2B) */}
          {showBusinessRegField && (
            <div class="form-group">
              <label class="form-label">
                Business Registration Number
                <span class="form-hint">(Optional)</span>
              </label>
              <input
                type="text"
                class="form-input"
                value={businessRegistration}
                onInput={e => setBusinessRegistration(e.target.value)}
                placeholder="Your business registration number"
              />
              <p class="form-help">
                For invoice purposes - will appear on your invoice
              </p>
            </div>
          )}

          {/* Address */}
          <div class="form-group">
            <label class="form-label">Address Line 1 *</label>
            <input
              type="text"
              class="form-input"
              value={addressLine1}
              onInput={e => setAddressLine1(e.target.value)}
              placeholder="Street address"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label">Address Line 2</label>
            <input
              type="text"
              class="form-input"
              value={addressLine2}
              onInput={e => setAddressLine2(e.target.value)}
              placeholder="Apartment, suite, etc. (optional)"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">City *</label>
              <input
                type="text"
                class="form-input"
                value={city}
                onInput={e => setCity(e.target.value)}
                placeholder="City"
                required
              />
            </div>
            <div class="form-group">
              <label class="form-label">Postal Code *</label>
              <input
                type="text"
                class="form-input"
                value={postalCode}
                onInput={e => setPostalCode(e.target.value)}
                placeholder="Postal code"
                required
              />
            </div>
          </div>

          {/* Contact Info */}
          <div class="form-group">
            <label class="form-label">Contact Email *</label>
            <input
              type="email"
              class="form-input"
              value={contactEmail}
              onInput={e => setContactEmail(e.target.value)}
              placeholder="billing@company.com"
              required
            />
            <p class="form-help">Invoices will be sent to this email</p>
          </div>

          <div class="form-group">
            <label class="form-label">Contact Name</label>
            <input
              type="text"
              class="form-input"
              value={contactName}
              onInput={e => setContactName(e.target.value)}
              placeholder="Billing contact name"
            />
          </div>
        </form>

        {/* Order Summary */}
        <div class="order-summary">
          <div class="summary-card">
            <h3>Order Summary</h3>

            <div class="summary-plan">
              <div class="plan-badge">{plan.name}</div>
              <p>{plan.description || `Up to ${plan.seats_limit || 'unlimited'} users`}</p>
            </div>

            <div class="summary-divider" />

            {pricing ? (
              <div class="summary-pricing">
                <div class="price-row">
                  <span>Subtotal</span>
                  <span>EUR {pricing.subtotal.toFixed(2)}</span>
                </div>
                <div class="price-row">
                  <span>VAT ({pricing.vat_rate_percent}%)</span>
                  <span>EUR {pricing.vat_amount.toFixed(2)}</span>
                </div>
                {pricing.vat_treatment !== 'standard' && (
                  <div class="vat-note">
                    {pricing.vat_treatment === 'reverse_charge' && 'Reverse charge applies'}
                    {pricing.vat_treatment === 'outside_scope' && 'Outside scope of EU VAT'}
                  </div>
                )}
                <div class="summary-divider" />
                <div class="price-row total">
                  <span>Total</span>
                  <span>EUR {pricing.total.toFixed(2)}/month</span>
                </div>
              </div>
            ) : (
              <div class="summary-loading">
                {pricingLoading ? 'Calculating...' : 'Enter details to see pricing'}
              </div>
            )}

            <button
              type="submit"
              class="btn btn-primary btn-block"
              onClick={handleSubmit}
              disabled={submitting || pricingLoading}
            >
              {submitting ? 'Processing...' : 'Continue to Payment'}
            </button>

            <div class="security-note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Secure payment via Stripe</span>
            </div>
          </div>

          <div class="guarantee-note">
            <strong>30-day money-back guarantee</strong>
            <p>Not satisfied? Get a full refund within 30 days.</p>
          </div>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .checkout-page {
    max-width: 1100px;
    margin: 0 auto;
  }

  .checkout-header {
    margin-bottom: 2rem;
  }

  .back-link {
    display: inline-block;
    color: var(--gray-500);
    text-decoration: none;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .back-link:hover {
    color: var(--primary);
  }

  .checkout-header h1 {
    font-size: 1.75rem;
    color: var(--gray-900);
    margin-bottom: 0.5rem;
  }

  .checkout-header p {
    color: var(--gray-500);
  }

  .loading-container {
    text-align: center;
    padding: 4rem;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--gray-200);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .checkout-denied {
    text-align: center;
    padding: 4rem;
    background: var(--bento-card-bg);
    border-radius: var(--bento-card-radius);
    box-shadow: var(--bento-card-shadow);
  }

  .alert {
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
  }

  .alert-error {
    background: #fef2f2;
    color: var(--danger);
    border: 1px solid #fee2e2;
  }

  .checkout-container {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 2rem;
    align-items: start;
  }

  @media (max-width: 900px) {
    .checkout-container {
      grid-template-columns: 1fr;
    }

    .order-summary {
      order: -1;
    }
  }

  .checkout-form {
    background: var(--bento-card-bg);
    border-radius: var(--bento-card-radius);
    box-shadow: var(--bento-card-shadow);
    padding: 2rem;
  }

  .form-section {
    margin-bottom: 1.5rem;
  }

  .toggle-group {
    display: flex;
    gap: 0;
    background: var(--gray-100);
    border-radius: 8px;
    padding: 4px;
  }

  .toggle-btn {
    flex: 1;
    padding: 0.75rem 1rem;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    color: var(--gray-600);
    transition: all 0.2s;
  }

  .toggle-btn.active {
    background: white;
    color: var(--gray-900);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-label {
    display: block;
    font-weight: 500;
    color: var(--gray-700);
    margin-bottom: 0.5rem;
  }

  .form-hint {
    font-weight: 400;
    color: var(--gray-400);
    font-size: 0.875rem;
    margin-left: 0.5rem;
  }

  .form-input,
  .form-select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--gray-300);
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    background: white;
  }

  .form-input:focus,
  .form-select:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .form-input.is-valid {
    border-color: var(--success);
  }

  .form-input.is-invalid {
    border-color: var(--danger);
  }

  .form-help {
    font-size: 0.875rem;
    color: var(--gray-500);
    margin-top: 0.5rem;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .input-with-button {
    display: flex;
    gap: 0.5rem;
  }

  .input-with-button .form-input {
    flex: 1;
  }

  .vat-success {
    color: var(--success);
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  .vat-error {
    color: var(--danger);
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  .order-summary {
    position: sticky;
    top: 2rem;
  }

  .summary-card {
    background: var(--bento-card-bg);
    border-radius: var(--bento-card-radius);
    box-shadow: var(--bento-card-shadow);
    padding: 1.5rem;
    margin-bottom: 1rem;
  }

  .summary-card h3 {
    font-size: 1.125rem;
    color: var(--gray-900);
    margin-bottom: 1rem;
  }

  .summary-plan {
    margin-bottom: 1rem;
  }

  .plan-badge {
    display: inline-block;
    background: var(--primary);
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .summary-plan p {
    color: var(--gray-500);
    font-size: 0.875rem;
    margin: 0;
  }

  .summary-divider {
    height: 1px;
    background: var(--gray-200);
    margin: 1rem 0;
  }

  .summary-pricing {
    margin-bottom: 1.5rem;
  }

  .price-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    color: var(--gray-600);
  }

  .price-row.total {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--gray-900);
    padding-top: 1rem;
  }

  .vat-note {
    font-size: 0.75rem;
    color: var(--gray-500);
    font-style: italic;
    padding: 0.25rem 0;
  }

  .summary-loading {
    text-align: center;
    padding: 1rem;
    color: var(--gray-400);
  }

  .btn {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
  }

  .btn-primary {
    background: var(--primary);
    color: white;
  }

  .btn-primary:hover {
    background: var(--primary-dark);
  }

  .btn-secondary {
    background: var(--gray-100);
    color: var(--gray-700);
  }

  .btn-secondary:hover {
    background: var(--gray-200);
  }

  .btn-block {
    width: 100%;
    padding: 1rem;
    font-size: 1rem;
  }

  .btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .security-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
    color: var(--gray-500);
    font-size: 0.875rem;
  }

  .security-note svg {
    width: 16px;
    height: 16px;
  }

  .guarantee-note {
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
  }

  .guarantee-note strong {
    color: var(--gray-900);
    display: block;
    margin-bottom: 0.25rem;
  }

  .guarantee-note p {
    color: var(--gray-600);
    font-size: 0.875rem;
    margin: 0;
  }
`;
